import { ValidationService,
         ClientValidationResult,
         PricingExcelApiResult,
         PromotionEntryExcelApiValidationRequest,
         PromotionEntryApiValidationRequestData,
         PromotionEntryExcelApiValidationResponse,
         PromotionEntryExcelDataRow,
         PromotionEntryExcelTemplate,
         ExcelPropertyInfo,
         DateDetails,
         DateMetadata, 
         ExcelViewerErrorData } from "types";
import { ClientValidationError, ClientValidationErrorType } from "errors";
import { commonRegex, dateFormatter } from "utils";
import { PromotionEntryExcelColumnHeaders, 
         PromotionEntryTemplateErrorKeys,
         YesOrNo } from "enums";
import { useSecurityService, useDateCheckerService, useExcelValidationHelpers } from "services";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { ExcelTemplateTypes } from "enums"; 

export function usePromotionEntryTemplateValidationService(id: string, fileName: string, changeNotes: string, postValidationCallback?: (args: any[]) => void): ValidationService {
    
    let excelViewerErrorData: ExcelViewerErrorData[] = [];

    const { getAuthHeader, getUserEmail } = useSecurityService();

    const { checkValidDay, 
            checkValidMonth, 
            checkValidYear,
            checkIsPastDate } = useDateCheckerService();

    const { checkRequiredField, 
            checkExcelHeaders,
            standardizePropertyNames,
            trimValuesLeadingAndTrailingWhitespace,
            tryParseInt: tryParseIntGeneric } = useExcelValidationHelpers();
    
    const tryParseInt = (prop: ExcelPropertyInfo) => tryParseIntGeneric(PromotionEntryExcelColumnHeaders, prop);

    const apiUrl = process.env.REACT_APP_SALES_PRICINGIMPORT_PROMOTION_ENTRY_VALIDATION_API,
          maxPricingDecimalPlaces = 4,
          regex = { dateFormat: commonRegex.dateFormats.mmddyyyy,
                    ambiguousDateMonth: commonRegex.dateFormats.monthFirstAmbiguous,
                    aboveMaxPrecision: commonRegex.maxDecimalPrecision(maxPricingDecimalPlaces),
                    hasNonNumeric: commonRegex.hasNonNumeric,
                    allNines: commonRegex.allNines,
                    customerId: commonRegex.customerId,
                    agreementId: commonRegex.agreementId,
                    employeeResponsibleName: /^[A-Za-z][A-Za-z .'\-]*$/ };

    //Sends a POST request containing uploaded and client-side validated excel data to Sales.PricingImport
    const postToValidationApi = async (clientValidatedExcel: PromotionEntryExcelDataRow[]) => {
        try {
            const outboundModel: PromotionEntryExcelApiValidationRequest = await convertToRequestModel(clientValidatedExcel);
            const fileId = `${fileName}-${id}.json`;
            const templateType = ExcelTemplateTypes.PromotionEntry;

            const config: AxiosRequestConfig = {
                headers: await getAuthHeader()
            };

            // Request a SAS Upload URL
            const sasResponse = await axios.post<{ BlobUrl: string }>(
                `${process.env.REACT_APP_SALES_PRICINGIMPORT_GENERATE_SAS_URL}`,
                { fileId, templateType },
                config
            );

            const sasUrl = sasResponse.data.BlobUrl;
            //console.log(`SAS URL for upload: ${sasUrl}`)

            // Upload JSON payload to Blob
            await axios.put(
                sasUrl,
                JSON.stringify(outboundModel),
                {
                    headers: {
                        "x-ms-blob-type": "BlockBlob",
                        "Content-Type": "application/json"
                    }
                }
            );

            const container = "pricing-template-submissions";
            const blobPath = `${templateType}/${fileId}`;

            // Notify backend (APIM) with blob reference
            const notifyBackendResponse = await axios.post(
                `${apiUrl}`,
                {
                    Container: container,
                    BlobPath: blobPath,
                    TemplateFileName: outboundModel.TemplateFileName,
                    SubmitterEmail: outboundModel.SubmitterEmail,
                    ChangeNotes: outboundModel.ChangeNotes,
                    CosmosGUID: outboundModel.CosmosGUID,
                },
                config
            );

            return {
                response: notifyBackendResponse.data as PromotionEntryExcelApiValidationResponse,
                postError: "",
                statusCode: notifyBackendResponse.status
            } as PricingExcelApiResult;

        } catch (ex: any) {
            const error = ex as AxiosError;
            const status = error.response?.status ?? 0;

            return {
                response: error.response?.data as PromotionEntryExcelApiValidationResponse,
                postError: error.message ?? "Error posting to validation API.",
                statusCode: status
            } as PricingExcelApiResult;
        }
    };   

    const doClientSideValidation = async (excel: PromotionEntryExcelTemplate[] | null, skipNumHeaders: number) => {
        let errText: string = "";
        const errorList: ExcelViewerErrorData[] = [],
              panelErrors: string[] = [],
              validFlags = Object.values<string>(YesOrNo);

        excelViewerErrorData = [];
        const extraHeaderValidation = () => {
            //Checking for data in a few fields of the first data row to ensure that this isn't a blank template
            if (!excel) return;
            const requiredTemplateColumns = [
                PromotionEntryTemplateErrorKeys.caseQuantity,
                PromotionEntryTemplateErrorKeys.serviceLocation
            ];
            const uploadedColumns = Object.keys(excel[0] ?? {});
            const missingTemplateColumns = requiredTemplateColumns.filter(column => !uploadedColumns.includes(column));

            if (missingTemplateColumns.length > 0) {
                throw new ClientValidationError(
                    ClientValidationErrorType.BadExcelHeaders,
                    "The uploaded Promotion Entry template is outdated.",
                    [
                        `Please download the latest Promotion Entry template and include the following columns: ${missingTemplateColumns.join(", ")}.`
                    ]);
            }

            if (skipNumHeaders < 1) {
                if (!(excel[0]["NEW/EXISTING"]
                     || excel[0]["PROMOTIONID"] 
                     || excel[0]["EXCEPTIONTYPE"] 
                     || excel[0]["COUNTRYCODE"]
                     || excel[0]["PROMOTIONID"] ) )
                {
                    throw new ClientValidationError(ClientValidationErrorType.NoData, 
                                                    "Excel file does not appear to contain any valid data rows.", 
                                                    null);
                }
            }
        };
        checkExcelHeaders(excel, skipNumHeaders, extraHeaderValidation);

        //Null suppression because the above call to checkExcelHeaders will
        //throw a ClientValidationError on null or empty excel data, so there's no need for another check.
        const excelData = convertToValidationModel(excel!, skipNumHeaders);

        //Loop through rows.  
        for (const [index, row] of excelData.entries()) {
            //1 to skip the header (keys) row, 1 to offset zero-indexing
            const rowNumber = index + 2 + skipNumHeaders;

            await doDateValidation(errorList, row, rowNumber);

            for (const prop of Object.getOwnPropertyNames(row)) {
                const thisCellValue = (`${row[prop as keyof PromotionEntryExcelDataRow] ?? ""}`).trim(),
                      propNameKey = prop as keyof typeof PromotionEntryExcelColumnHeaders,
                      propColumnName = `column "${PromotionEntryExcelColumnHeaders[propNameKey]}"`,
                      errorKey = prop as keyof typeof PromotionEntryTemplateErrorKeys,
                      thisPropInfo = { propName: propNameKey,
                                       value: thisCellValue,
                                       dataRow: rowNumber,
                                       errorKey: errorKey,
                                       log: errorList } as ExcelPropertyInfo;
                switch (prop) {
                    case "newOrExisting":
                        checkIfRequiredFieldIsNotEmpty(thisPropInfo);
                        break;
                    case "customerId":
                        if (!checkIfRequiredFieldIsNotEmpty(thisPropInfo)) {
                            break;
                        } else if (
                            thisCellValue &&
                            thisCellValue !== "*" &&
                            !/^\d+-\d+$/.test(thisCellValue)
                        ) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, must be either "*" or in format "digits-digits" (e.g., "123456-0").`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        }
                        break;
                    case "promotionId":
                        checkIfRequiredFieldIsNotEmpty(thisPropInfo);
                        break;
                    case "itemNumber":
                    case "productLine":
                    case "productLineCategory":
                        if (thisCellValue !== "" && thisCellValue !== "*" && parseInt(thisCellValue) < 0) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, cannot be a negative number.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys[errorKey], [errText]));
                        } else if (thisCellValue && regex.hasNonNumeric.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, ` +
                                      `cannot contain non-numeric characters.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        } 
                        break;
                    case "promotionAmount":
                    case "newPromotionAmount":
                    case "promotionPercent":
                        if (thisCellValue && regex.hasNonNumeric.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, ` +
                                      `cannot contain non-numeric characters.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        } else if (regex.aboveMaxPrecision.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, this field can have no more ` + 
                                      `than ${maxPricingDecimalPlaces} decimal places.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                    case "allowMultiple":
                    case "supplierFlag":
                        if (thisCellValue !== "" && validFlags.findIndex(x => x.toLowerCase() === thisCellValue.toLowerCase()) === -1) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, this field must contain ` + 
                                      `one of the following values: [${validFlags.join(", ")} or true, false]`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                    case "employee":
                        if (thisCellValue !== "" && !regex.employeeResponsibleName.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, must contain only letters, spaces, apostrophes, hyphens, or periods.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        }
                        break;
                    case "jobType":
                        const trimmedValue = String(thisCellValue).trim();

                        if (trimmedValue !== "" && !/^\d+$/.test(trimmedValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}. Must be blank or a non-negative whole number.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                    case "caseQuantity":
                    case "serviceLocation":
                        if (thisCellValue && regex.hasNonNumeric.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, ` +
                                      `cannot contain non-numeric characters.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys[errorKey], [errText]));
                        } else if (thisCellValue && !/^\d+$/.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, must be a whole number.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys[errorKey], [errText]));
                        } else if (thisCellValue && parseInt(thisCellValue) <= 0) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, must be greater than zero.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                }
            }
        }
        
        //When we're done looping through rows
        if (postValidationCallback) 
            postValidationCallback([...excelViewerErrorData, ...errorList]);

        errorList.forEach(x => panelErrors.push(`Data row ${x.row} - ${x.info.join(" | ")}`));
        
        return { requestData: excelData, 
                 errorList: panelErrors, 
                 errorCount: panelErrors.length } as ClientValidationResult;
    };
    
    //Strips spaces and lower case letters out of Excel template property names
    const standardizeExcelPropertyNameFormat = (rawExcel: any[]) => standardizePropertyNames<PromotionEntryExcelTemplate>(rawExcel);

    //Logs error on missing required field 
    const checkIfRequiredFieldIsNotEmpty = (props: ExcelPropertyInfo) => checkRequiredField(PromotionEntryExcelColumnHeaders, props);
        
    //Checks for invalid date formats, past dates, end dates before begin dates
    const doDateValidation = async (errors: ExcelViewerErrorData[], model: PromotionEntryExcelDataRow, rowNumber: number) => {
        const format: RegExp = regex.dateFormat,
              ambiguousMatch: RegExp = regex.ambiguousDateMonth,
              beginOrderErrors: ExcelViewerErrorData[] = [],
              endOrderErrors: ExcelViewerErrorData[] = [],

              beginDeliveryErrors: ExcelViewerErrorData[] = [],
              endDeliveryErrors: ExcelViewerErrorData[] = [],

              beginOrder = model.beginOrderDate?.toString().match(format),
              endOrder = model.endOrderDate?.toString().match(format),

              beginDelivery = model.beginDeliveryDate?.toString().match(format),
              endDelivery = model.endDeliveryDate?.toString().match(format),

              bothOrderColumns = `${PromotionEntryTemplateErrorKeys.beginOrderDate}, ` +
                  `${PromotionEntryTemplateErrorKeys.endOrderDate}`,
              bothDeliveryColumns = `${PromotionEntryTemplateErrorKeys.beginDeliveryDate}, ` +
                  `${PromotionEntryTemplateErrorKeys.endDeliveryDate}`;

        let errText: string = "";

        const beginOrderDate = {
                year: parseInt(beginOrder?.groups?.year ?? ""),
                month: parseInt(beginOrder?.groups?.month ?? ""),
                day: parseInt(beginOrder?.groups?.day ?? "")
            } as DateDetails,

            endOrderDate = {
                year: parseInt(endOrder?.groups?.year ?? ""),
                month: parseInt(endOrder?.groups?.month ?? ""),
                day: parseInt(endOrder?.groups?.day ?? "")
            } as DateDetails,

            beginDeliveryDate = {
                year: parseInt(beginDelivery?.groups?.year ?? ""),
                month: parseInt(beginDelivery?.groups?.month ?? ""),
                day: parseInt(beginDelivery?.groups?.day ?? "")
            } as DateDetails,

            endDeliveryDate = {
                year: parseInt(endDelivery?.groups?.year ?? ""),
                month: parseInt(endDelivery?.groups?.month ?? ""),
                day: parseInt(endDelivery?.groups?.day ?? "")
            } as DateDetails,

            beginOrderMetadata = {
                fullDateString: model.beginOrderDate,
                date: beginOrderDate,
                dateType: PromotionEntryTemplateErrorKeys.beginOrderDate,
                dateColumnName: PromotionEntryTemplateErrorKeys.beginOrderDate,
                rowNumber: rowNumber
            } as DateMetadata,

            endOrderMetadata = {
                fullDateString: model.endOrderDate,
                date: endOrderDate,
                dateType: PromotionEntryTemplateErrorKeys.endOrderDate,
                dateColumnName: PromotionEntryTemplateErrorKeys.endOrderDate,
                rowNumber: rowNumber
            } as DateMetadata,

            beginDeliveryMetadata = {
                fullDateString: model.beginDeliveryDate,
                date: beginDeliveryDate,
                dateType: PromotionEntryTemplateErrorKeys.beginDeliveryDate,
                dateColumnName: PromotionEntryTemplateErrorKeys.beginDeliveryDate,
                rowNumber: rowNumber
            } as DateMetadata,

            endDeliveryMetadata = {
                fullDateString: model.endDeliveryDate,
                date: endDeliveryDate,
                dateType: PromotionEntryTemplateErrorKeys.endDeliveryDate,
                dateColumnName: PromotionEntryTemplateErrorKeys.endDeliveryDate,
                rowNumber: rowNumber
            } as DateMetadata;

        const flags = { sameYear: beginOrderDate.year === endOrderDate.year,
                        sameMonth: (beginOrderDate.year === endOrderDate.year && beginOrderDate.month === endOrderDate.month),
                        invalidMonth: false,
                        invalidYear: false,
                        invalidDay: false,
                        badFormat: false,

                        badBeginOrderDate: false,
                        badEndOrderDate: false,

                        badBeginDeliveryDate: false,
                        badEndDeliveryDate: false,

                        allNinesEndOrderDate: false,
                        allNinesEndDeliveryDate: false};

        //beginOrderDate checks
        if (model.beginOrderDate) {
            flags.invalidYear = !checkValidYear(beginOrderMetadata, beginOrderErrors);
            flags.invalidMonth = !checkValidMonth(beginOrderMetadata, beginOrderErrors);
            flags.invalidDay = !checkValidDay(beginOrderMetadata, beginOrderErrors, flags.invalidMonth, flags.invalidYear);

            const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

            flags.badFormat = badDateFormat;
            flags.badBeginOrderDate = badDateFormat;
        }

        //endOrderDate checks
        if (model.endOrderDate) {
            flags.allNinesEndOrderDate = regex.allNines.test(model.endOrderDate);
            if (flags.allNinesEndOrderDate) {
                flags.badEndOrderDate = true;
                errText = `Invalid end date of 99999999. Please use 12319999 instead.`
                errors.push(new ExcelViewerErrorData(rowNumber, bothOrderColumns, [errText]));
            }

            if (!flags.allNinesEndOrderDate) {
                //checkIsPastDate(endOrderMetadata, endOrderErrors);
                flags.invalidYear = !checkValidYear(endOrderMetadata, endOrderErrors);
                flags.invalidMonth = !checkValidMonth(endOrderMetadata, endOrderErrors);
                flags.invalidDay = !checkValidDay(endOrderMetadata, endOrderErrors, flags.invalidMonth, flags.invalidYear);
                
                const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

                flags.badEndOrderDate = badDateFormat;
                flags.badFormat = flags.badFormat || badDateFormat;
                
                //Date range checks
                if( (flags.sameYear && beginOrderDate.month > endOrderDate.month)
                    || (flags.sameMonth && beginOrderDate.day > endOrderDate.day)
                    || (endOrderDate.year < beginOrderDate.year) ) {
                    errText = `Invalid date range of "${model.beginOrderDate}" - "${model.endOrderDate}", ` + 
                              `reason: end order date is before begin order date.`
                    errors.push(new ExcelViewerErrorData(rowNumber, bothOrderColumns, [errText]));
                }
            }
        }

        //beginDeliveryDate checks
        if (model.beginDeliveryDate) {
            flags.invalidYear = !checkValidYear(beginDeliveryMetadata, beginDeliveryErrors);
            flags.invalidMonth = !checkValidMonth(beginDeliveryMetadata, beginDeliveryErrors);
            flags.invalidDay = !checkValidDay(beginDeliveryMetadata, beginDeliveryErrors, flags.invalidMonth, flags.invalidYear);

            const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

            flags.badFormat = badDateFormat;
            flags.badBeginDeliveryDate = badDateFormat;
        }

        //endDeliveryDate checks
        if (model.endDeliveryDate) {
            flags.allNinesEndDeliveryDate = regex.allNines.test(model.endDeliveryDate);
            if (flags.allNinesEndDeliveryDate) {
                flags.badEndDeliveryDate = true;
                errText = `Invalid end date of 99999999. Please use 12319999 instead.`
                errors.push(new ExcelViewerErrorData(rowNumber, bothOrderColumns, [errText]));
            }

            if (!flags.allNinesEndDeliveryDate) {
                //checkIsPastDate(endDeliveryMetadata, endDeliveryErrors);                
                flags.sameYear = beginDeliveryDate.year === endDeliveryDate.year;
                flags.sameMonth = (beginDeliveryDate.year === endDeliveryDate.year && beginDeliveryDate.month === endDeliveryDate.month);
                flags.invalidYear = !checkValidYear(endDeliveryMetadata, endDeliveryErrors);
                flags.invalidMonth = !checkValidMonth(endDeliveryMetadata, endDeliveryErrors);
                flags.invalidDay = !checkValidDay(endDeliveryMetadata, endDeliveryErrors, flags.invalidMonth, flags.invalidYear);
                
                const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

                flags.badEndDeliveryDate = badDateFormat;
                flags.badFormat = flags.badFormat || badDateFormat;

                //Date range checks
                if( (flags.sameYear && beginDeliveryDate.month > endDeliveryDate.month)
                    || (flags.sameMonth && beginDeliveryDate.day > endDeliveryDate.day)
                    || (endDeliveryDate.year < beginDeliveryDate.year) ) {
                    errText = `Invalid date range of "${model.beginDeliveryDate}" - "${model.endDeliveryDate}", ` + 
                              `reason: end delivery date is before begin delivery date.`
                    errors.push(new ExcelViewerErrorData(rowNumber, bothDeliveryColumns, [errText]));
                }
            }
        }
              
        //Ambiguity checks    
        const hasAmbiguousBeginOrderDate = ambiguousMatch.test(model.beginOrderDate?.toString() ?? ""),
              hasAmbiguousEndOrderDate = !flags.allNinesEndOrderDate && ambiguousMatch.test(model.endOrderDate?.toString() ?? ""),
              hasAmbiguousBeginDeliveryDate = ambiguousMatch.test(model.beginDeliveryDate?.toString() ?? ""),
              hasAmbiguousEndDeliveryDate = !flags.allNinesEndDeliveryDate && ambiguousMatch.test(model.endDeliveryDate?.toString() ?? "");

        if (hasAmbiguousBeginOrderDate) {
            flags.badBeginOrderDate = true;
            errText = "Begin delivery date is ambiguous - add forward-slash ('/') separators between month, day, and year, " + 
                      "or use the full 8-digit MMDDYYYY format with leading zeroes for month and/or day where appropriate.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys.beginOrderDate, [errText]));
        }
        if (hasAmbiguousEndOrderDate) {
            flags.badEndOrderDate = true;
            errText = "End delivery date is ambiguous - add forward-slash ('/') separators between month, day, and year, " + 
                      "or use the full 8-digit MMDDYYYY format with leading zeroes for month and/or day where appropriate.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys.endOrderDate, [errText]));
        }
        if (hasAmbiguousBeginDeliveryDate) {
            flags.badBeginDeliveryDate = true;
            errText = "Begin delivery date is ambiguous - add forward-slash ('/') separators between month, day, and year, " + 
                      "or use the full 8-digit MMDDYYYY format with leading zeroes for month and/or day where appropriate.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys.beginDeliveryDate, [errText]));
        }
        if (hasAmbiguousEndDeliveryDate) {
            flags.badEndDeliveryDate = true;
            errText = "End delivery date is ambiguous - add forward-slash ('/') separators between month, day, and year, " + 
                      "or use the full 8-digit MMDDYYYY format with leading zeroes for month and/or day where appropriate.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys.endDeliveryDate, [errText]));
        }

        //Aggregation
        if (flags.badBeginOrderDate) {
            errText = "Begin order date appears not to be in correct format.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys.beginOrderDate, [errText]));
        } else {
            //These errors are only valuable if begin date format is correct
            errors.push(...beginOrderErrors);
        }

        if (flags.badEndOrderDate) {
            errText = 'End order date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys.endOrderDate, [errText]));
        } else {
            //These errors are only valuable if end date format is correct
            errors.push(...endOrderErrors);
        }
        
        if (flags.badBeginDeliveryDate) {
            errText = "Begin delivery date appears not to be in correct format.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys.beginDeliveryDate, [errText]));
        } else {
            //These errors are only valuable if begin date format is correct
            errors.push(...beginDeliveryErrors);
        }

        if (flags.badEndDeliveryDate) {
            errText = 'End delivery date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionEntryTemplateErrorKeys.endDeliveryDate, [errText]));
        } else {
            //These errors are only valuable if end date format is correct
            errors.push(...endDeliveryErrors);
        }
        
        if (flags.badFormat) {
            errText = `Ensure that all dates are in the correct format: MMDDYYYY`;
            errors.push(new ExcelViewerErrorData(rowNumber, "", [errText]));
        }

        //Because we're accepting MMDDYYYY values for months 1-9 even without a leading zero, 
        //we need to standardize the format for the validation API
        if (model.beginOrderDate) 
            model.beginOrderDate = dateFormatter.MMDDYYYY(beginOrderDate);

        if (model.endOrderDate && !flags.allNinesEndOrderDate) 
            model.endOrderDate = dateFormatter.MMDDYYYY(endOrderDate);

        if (model.beginDeliveryDate) 
            model.beginDeliveryDate = dateFormatter.MMDDYYYY(beginDeliveryDate);

        if (model.endDeliveryDate && !flags.allNinesEndDeliveryDate) 
            model.endDeliveryDate = dateFormatter.MMDDYYYY(endDeliveryDate);
    };
    
    //Model conversions
    const convertToValidationModel = (rawExcelTemplate: PromotionEntryExcelTemplate[], skipNumHeaders: number): PromotionEntryExcelDataRow[] => {
        let validationModel: PromotionEntryExcelDataRow[] = [];
        const excelTemplate = trimValuesLeadingAndTrailingWhitespace<PromotionEntryExcelTemplate>(rawExcelTemplate);

        for (let i = skipNumHeaders; i < excelTemplate.length; i++) {
            const fieldIsEmpty = (prop: ExcelPropertyInfo) => (prop.value?.toString().trim().length ?? 0) === 0,
                  tryParseIfNotNull = (prop: ExcelPropertyInfo) => (fieldIsEmpty(prop) ? null : tryParseInt(prop)),
                  rowData = {} as PromotionEntryExcelDataRow,
                  excelData = excelTemplate[i],
                  propInfo = { propName: "",
                               value: null,
                               dataRow: i + 2,
                               log: [] } as ExcelPropertyInfo;

            let customerNumber, 
                customerShipToNumber;

            for (const property of Object.getOwnPropertyNames(excelData)) {
                const key = property as keyof typeof excelData;
                if (excelData[key] && typeof(excelData[key] === 'string')) {
                    excelData[key] = (`${excelData[key]}`).trim();
                }
            }

            //Non-integer fields
            const customerId = excelData.CUSTOMERID ?? "";
            if (customerId && customerId !== "*") 
            {
                const parsedCustomer = regex.customerId.exec(customerId)?.groups;
                if (parsedCustomer) {
                    customerNumber = parsedCustomer["customer"];
                    customerShipToNumber = parsedCustomer["shipto"];
                }
            }

            rowData.newOrExisting = excelData["NEW/EXISTING"] ?? "";
            rowData.promotionId = excelData.PROMOTIONID ?? "";
            rowData.promotionDescription = excelData.PROMOTIONDESC ?? "";
            rowData.exceptionType = excelData.EXCEPTIONTYPE ?? "";

            rowData.customerId = customerId;
            rowData.customerName = excelData.CUSTOMERNAME ?? "";

            rowData.countryCode = excelData.COUNTRYCODE ?? "";
            rowData.corporateAffiliation = excelData.CORPORATEAFFILIATION ?? "";
            rowData.freightZone = excelData.FREIGHTZONE ?? "";

            rowData.manufacturerNumber = excelData.MANUFACTURERSNUMBER ?? "";
            rowData.gtin = excelData.GTIN ?? "";
            rowData.allowMultiple = excelData.ALLOWMULTIPLE ?? "";

            rowData.supplierFlag = excelData.SUPPLIERFLAG ?? "";

            rowData.promotionAmount = excelData.PROMOTIONAMOUNT ?? "";
            rowData.uom = excelData.UOM ?? "";
            rowData.weightType = excelData.WEIGHTTYPE ?? "";
            rowData.promotionPercent = excelData.PROMOTIONPERCENT ?? "";

            rowData.minimumValue = excelData.MINIMUMVALUE ?? "";
            rowData.maximumValue = excelData.MAXIMUMVALUE ?? "";
            
            rowData.beginOrderDate = excelData.BEGINORDERDATE ?? "";
            rowData.endOrderDate = excelData.ENDORDERDATE ?? "";
            rowData.beginDeliveryDate = excelData.BEGINDELIVERYDATE ?? "";
            rowData.endDeliveryDate = excelData.ENDDELIVERYDATE ?? "";
            rowData.serviceLocation = excelData.SERVICELOCATION ?? "";

            let rawFlag = excelData.SUPPLIERFLAG?.toString().trim().toLowerCase();
            if (rawFlag === "true") {
                rowData.supplierFlag = "Y";
            } else if (rawFlag === "false") {
                rowData.supplierFlag = "N";
            } else {
                rowData.supplierFlag = excelData.SUPPLIERFLAG ?? "";
            }

            //Defaulting these to zero seemed like maybe a bad idea
            rowData.promotionAmount = excelData.PROMOTIONAMOUNT;
            rowData.promotionPercent = excelData.PROMOTIONPERCENT;

            //Integer fields

            //Promotion Type
            propInfo.propName = PromotionEntryTemplateErrorKeys.promotionType;
            propInfo.value = excelData.PROMOTIONTYPE;
            rowData.promotionType = tryParseIfNotNull(propInfo);

            //Promotion Category
            propInfo.propName = PromotionEntryTemplateErrorKeys.promotionCategory;
            propInfo.value = excelData.PROMOTIONCATEGORY;
            rowData.promotionCategory = tryParseIfNotNull(propInfo);

            //Segment
            propInfo.propName = PromotionEntryTemplateErrorKeys.segment;
            propInfo.value = excelData.SEGMENT;
            rowData.segment = tryParseIfNotNull(propInfo);

            //Channel
            propInfo.propName = PromotionEntryTemplateErrorKeys.channel;
            propInfo.value = excelData.CHANNEL;
            rowData.channel = tryParseIfNotNull(propInfo);

            //Tier
            propInfo.propName = PromotionEntryTemplateErrorKeys.tier;
            propInfo.value = excelData.TIER;
            rowData.tier = tryParseIfNotNull(propInfo);

            //Buying group
            propInfo.propName = PromotionEntryTemplateErrorKeys.buyingGroup;
            propInfo.value = excelData.BUYINGGROUP;
            rowData.buyingGroup = tryParseIfNotNull(propInfo);

            //Corporate affiliation
            propInfo.propName = PromotionEntryTemplateErrorKeys.corporateAffiliation;
            propInfo.value = excelData.CORPORATEAFFILIATION;
            rowData.corporateAffiliation = tryParseIfNotNull(propInfo);

            //Price zone
            propInfo.propName = PromotionEntryTemplateErrorKeys.priceZone;
            propInfo.value = excelData.PRICEZONE;
            rowData.priceZone = tryParseIfNotNull(propInfo);

            //Product line number
            propInfo.propName = PromotionEntryTemplateErrorKeys.productLine;
            propInfo.value = excelData.PRODUCTLINE;
            if (propInfo.value && propInfo.value !== "*") {
                rowData.productLine = tryParseIfNotNull(propInfo);
            }

            //Product category number
            propInfo.propName = PromotionEntryTemplateErrorKeys.productLineCategory;
            propInfo.value = excelData.PRODUCTCATEGORY;
            if (propInfo.value && propInfo.value !== "*") {
                rowData.productLineCategory = tryParseIfNotNull(propInfo);
            }

            //Item number
            propInfo.propName = PromotionEntryTemplateErrorKeys.itemNumber;
            propInfo.value = excelData.ITEMNUMBER;
            if (propInfo.value && propInfo.value !== "*") {
                rowData.itemNumber = tryParseIfNotNull(propInfo);
            }

            //Employee Responsible Name
            rowData.employee = excelData.EMPLOYEERESPONSIBLEPRICING ?? "";

            //Job type
            propInfo.propName = PromotionEntryTemplateErrorKeys.jobType;
            propInfo.value = excelData.JOBTYPE;
            rowData.jobType = tryParseIfNotNull(propInfo);
            
            //Minimum code
            propInfo.propName = PromotionEntryTemplateErrorKeys.minimumCode;
            propInfo.value = excelData.MINIMUMCODE;
            rowData.minimumCode = tryParseIfNotNull(propInfo);
            
            //Maximum code
            propInfo.propName = PromotionEntryTemplateErrorKeys.maximumCode;
            propInfo.value = excelData.MAXIMUMCODE;
            rowData.maxCode = tryParseIfNotNull(propInfo);

            //Promotion Adjustment
            propInfo.propName = PromotionEntryTemplateErrorKeys.promotionAdjustment;
            propInfo.value = excelData.PROMOTIONADJUSTMENT;
            rowData.promotionAdjustment = tryParseIfNotNull(propInfo);

            rowData.caseQuantity = excelData.CASEQUANTITY ?? "";

            if (propInfo.log && propInfo.log.length > 0) {
                excelViewerErrorData.push(...propInfo.log);
            }

            validationModel.push(rowData);
        }
        return validationModel;
    };

    
    //Client validation model => validation API request model
    const convertToRequestModel = (validatedExcel: PromotionEntryExcelDataRow[]) => {
        const requestTemplate = validatedExcel.map(row => {
                const caseQuantity = row.caseQuantity?.toString().trim() ?? "",
                      serviceLocation = row.serviceLocation?.toString().trim() ?? "";
                return { NewOrExisting: `${row.newOrExisting}`,
                         PromotionId: `${row.promotionId}`,
                         PromotionIdDescription: `${row.promotionDescription}`,
                         PromotionType: row.promotionType || null,
                         PromotionCategory: row.promotionCategory || null,
                         ExceptionType: row.exceptionType || null,

                         CustomerId: row.customerId || null,
                         CustomerName: `${row.customerName}`,

                         CountryCode: `${row.countryCode}`,
                         Segment: row.segment || null,
                         Channel: row.channel || null,
                         Tier: row.tier || null,
                         BuyingGroup: row.buyingGroup || null,
                         CorporateAffiliation: row.corporateAffiliation?.toString() || null,
                         PriceZone: row.priceZone || null,
                         FreightZone: `${row.freightZone}`,
                    
                         ProductLine: row.productLine || null,
                         ProductLineCategory: row.productLineCategory || null,

                         ItemNumber: row.itemNumber || null,
                         ManufacturerNumber: `${row.manufacturerNumber}`,
                         Gtin: `${row.gtin}`,

                         AllowMultiple: `${row.allowMultiple}`,

                         EmployeeResponsibleName: row.employee?.toString().trim() || null,
                         JobType: row.jobType || null,
                         SupplierIndicator: row.supplierFlag || "N",
                         
                         PromotionAmount: row.promotionAmount || "0",
                         UnitOfMeasure: `${row.uom}`,
                         WeightType: `${row.weightType}`,
                         PromotionPercentage: row.promotionPercent || "0",

                         MinimumValue: `${row.minimumValue}`,
                         MinimumCode: row.minimumCode || null,

                         MaximumValue: `${row.maximumValue}`,
                         MaximumCode: row.maxCode || null,

                         BeginOrderDate: `${row.beginOrderDate}`,
                         EndOrderDate: `${row.endOrderDate}`,
                         BeginDeliveryDate: `${row.beginDeliveryDate}`,
                         EndDeliveryDate: `${row.endDeliveryDate}`,

                         PromotionAdjustment: row.promotionAdjustment || null,
                         CaseQuantity: caseQuantity ? parseInt(caseQuantity) : null,
                         ServiceLocation: serviceLocation ? parseInt(serviceLocation) : null,
                     } as PromotionEntryApiValidationRequestData;
                }
        );

        const requestModel: PromotionEntryExcelApiValidationRequest = 
        {
            TemplateFileName: fileName,
            CosmosGUID: id,
            SubmitterEmail: getUserEmail(),
            ChangeNotes: changeNotes,
            PromotionEntryTemplate: requestTemplate
        };

        return requestModel;
    };

    
    //Exports
    const testingExports = { 
        doDateValidation,
        convertToValidationModel,
        convertToRequestModel 
    };

    return { 
        doClientSideValidation, 
        postToValidationApi,
        standardizeExcelPropertyNameFormat,
        testingExports 
    };
}
